import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Trash2, Download, FileText, ChevronDown, ChevronLeft, ChevronRight, Search } from 'react-feather';
import config from '../../../../config';
import '../../sadmin.css';

const ITEMS_PER_PAGE = 50;

const DataDeleteLog = () => {
    const { t } = useTranslation();
    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');

    const token = localStorage.getItem('sessiontoken');
    const configheader = { headers: { Authorization: `${token}` } };

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportData, setReportData] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [validationMessage, setValidationMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [concessionaires, setConcessionaires] = useState([]);
    const [selectedConcessionaire, setSelectedConcessionaire] = useState('');
    const [query, setQuery] = useState('');

    const today = new Date().toISOString().split('T')[0];

    useEffect(() => {
        axios
            .get(`${config.hostname}/concessionaire`, configheader)
            .then((res) => setConcessionaires(res.data || []))
            .catch((err) => console.error(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return reportData;
        return reportData.filter((it) =>
            [
                it.concessionaire_code,
                it.mall_code,
                it.tenant_code,
                it.tenant_name,
                it.reason,
                it.daterange,
                it.created_by,
            ]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
        );
    }, [reportData, query]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const currentPageData = filtered.slice(currentPage * ITEMS_PER_PAGE, (currentPage + 1) * ITEMS_PER_PAGE);

    useEffect(() => {
        if (currentPage >= pageCount) setCurrentPage(0);
    }, [currentPage, pageCount]);

    const insertToLog = (task) => {
        axios
            .post(
                `${config.hostname}/log`,
                { created_by: login_id, module: 'Super Admin', task: `${login_name}${task}` },
                configheader,
            )
            .catch((err) => console.error(err));
    };

    const generateReport = async (e) => {
        e.preventDefault();
        if (endDate < startDate) {
            setValidationMessage(t('End date cannot be less than start date'));
            return;
        }
        setValidationMessage('');
        const selectedCode = concessionaires.find((c) => c._id === selectedConcessionaire)?.concessionaire_code;
        setLoading(true);
        try {
            const res = await axios.post(
                `${config.hostname}/log/deletionlog`,
                { start_date: startDate, end_date: endDate, concessionaire_code: selectedCode },
                configheader,
            );
            setReportData(res.data || []);
            setCurrentPage(0);
            insertToLog(' generated deletion log report');
        } catch (err) {
            console.error(err);
            toast.error(t('Error'));
        } finally {
            setLoading(false);
        }
    };

    const buildExportRows = () =>
        reportData.map((it) => ({
            Date: moment(it.created_date).format('DD/MM/YYYY HH:mm:ss'),
            'Concessionaire Code': it.concessionaire_code || '',
            'Mall Code': it.mall_code || '',
            'Tenant Code': it.tenant_code || '',
            'Tenant Name': it.tenant_name || '',
            Reason: it.reason || '',
            'Deleted Dates': it.daterange || '',
            'Deleted By': it.created_by || '',
        }));

    const handleExportExcel = () => {
        if (reportData.length === 0) return;
        const ws = XLSX.utils.json_to_sheet(buildExportRows());
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Deletion Log');
        XLSX.writeFile(wb, 'DeletionLog.xlsx');
        insertToLog(' exported deletion log to excel');
    };

    const handleExportPDF = () => {
        if (reportData.length === 0) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(13);
        doc.text('Deletion Log', doc.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
        doc.setFontSize(8);
        doc.text(`From: ${startDate}  To: ${endDate}`, 10, 20);
        const rows = buildExportRows();
        const headers = Object.keys(rows[0] || {});
        doc.autoTable({
            startY: 26,
            head: [headers],
            body: rows.map((r) => headers.map((h) => r[h])),
            styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
        });
        doc.save('DeletionLog.pdf');
        insertToLog(' exported deletion log to pdf');
    };

    return (
        <div className="v3 v3-sadmin">
            <header className="v3-sadmin-hero">
                <div className="v3-sadmin-hero-text">
                    <div className="v3-label">{t('Settings · Deletions')}</div>
                    <h1 className="v3-display v3-sadmin-greeting" style={{ fontSize: 26 }}>
                        {t('Deletion log')}
                    </h1>
                    <p className="v3-body v3-sadmin-sub">
                        {t('Audit trail of sales data deletions. Filter by date range and mall.')}
                    </p>
                </div>
            </header>

            <section className="v3-card v3-sadmin-form-card">
                <form className="v3-sadmin-form" onSubmit={generateReport}>
                    <div className="v3-sadmin-form-grid-3">
                        <div className="v3-field">
                            <label className="v3-label">{t('Start date')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={startDate}
                                max={today}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                    setReportData([]);
                                    setCurrentPage(0);
                                }}
                                required
                            />
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('End date')}</label>
                            <input
                                className="v3-input"
                                type="date"
                                value={endDate}
                                max={today}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                    setReportData([]);
                                    setCurrentPage(0);
                                }}
                                required
                            />
                            {validationMessage && (
                                <span className="v3-sadmin-field-err">{validationMessage}</span>
                            )}
                        </div>
                        <div className="v3-field">
                            <label className="v3-label">{t('Mall')}</label>
                            <div className="v3-select-wrap">
                                <select
                                    className="v3-input v3-select"
                                    value={selectedConcessionaire}
                                    onChange={(e) => {
                                        setSelectedConcessionaire(e.target.value);
                                        setReportData([]);
                                        setCurrentPage(0);
                                    }}
                                >
                                    <option value="">{t('All malls')}</option>
                                    {concessionaires.map((c) => (
                                        <option key={c._id} value={c._id}>
                                            {c.concessionaire_name}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={14} className="v3-select-caret" aria-hidden />
                            </div>
                        </div>
                    </div>
                    <div className="v3-sadmin-form-actions">
                        <button type="submit" className="v3-btn v3-btn-primary" disabled={loading}>
                            <Trash2 size={14} />
                            {loading ? t('Generating…') : t('Generate report')}
                        </button>
                    </div>
                </form>
            </section>

            <section className="v3-card v3-sadmin-list-card">
                <div className="v3-sadmin-list-header">
                    <div className="v3-sadmin-list-title">
                        <h2 className="v3-heading">{t('Deletions')}</h2>
                        <span className="v3-sadmin-list-count">{filtered.length}</span>
                    </div>
                    <div className="v3-sadmin-list-actions">
                        <div className="v3-sadmin-search">
                            <Search size={14} aria-hidden />
                            <input
                                type="search"
                                placeholder={t('Search tenant, mall, reason, user')}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setCurrentPage(0);
                                }}
                                disabled={reportData.length === 0}
                                aria-label={t('Search results')}
                            />
                        </div>
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleExportExcel}
                            disabled={reportData.length === 0}
                        >
                            <Download size={14} /> {t('Excel')}
                        </button>
                        <button
                            type="button"
                            className="v3-btn v3-btn-ghost"
                            onClick={handleExportPDF}
                            disabled={reportData.length === 0}
                        >
                            <FileText size={14} /> {t('PDF')}
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="v3-sadmin-empty">
                        <p>{t('Loading…')}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="v3-sadmin-empty">
                        <Trash2 size={28} aria-hidden />
                        <h3>{reportData.length === 0 ? t('No deletions') : t('No matches')}</h3>
                        <p>
                            {reportData.length === 0
                                ? t('Generate a report for the chosen window.')
                                : t('Try a different search term.')}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="v3-sadmin-table-wrap">
                            <table className="v3-sadmin-table">
                                <thead>
                                    <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Concessionaire')}</th>
                                        <th>{t('Mall')}</th>
                                        <th>{t('Tenant')}</th>
                                        <th>{t('Reason')}</th>
                                        <th>{t('Deleted dates')}</th>
                                        <th>{t('Deleted by')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPageData.map((it, i) => (
                                        <tr key={i} style={{ cursor: 'default' }}>
                                            <td className="v3-sadmin-cell-muted">
                                                {moment(it.created_date).format('DD/MM/YYYY HH:mm:ss')}
                                            </td>
                                            <td>
                                                <span className="v3-sadmin-cell-code">{it.concessionaire_code}</span>
                                            </td>
                                            <td>
                                                <span className="v3-sadmin-cell-code">{it.mall_code}</span>
                                            </td>
                                            <td className="v3-sadmin-cell-strong">
                                                {it.tenant_name}{' '}
                                                <span className="v3-sadmin-cell-muted">({it.tenant_code})</span>
                                            </td>
                                            <td className="v3-sadmin-cell-muted">{it.reason}</td>
                                            <td className="v3-sadmin-cell-muted" style={{ maxWidth: 320 }}>
                                                {it.daterange}
                                            </td>
                                            <td>{it.created_by}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {filtered.length > ITEMS_PER_PAGE && (
                            <div className="v3-sadmin-pagination">
                                <span>
                                    {t('Page')} <strong>{currentPage + 1}</strong> {t('of')} <strong>{pageCount}</strong>
                                </span>
                                <div className="v3-sadmin-pagination-controls">
                                    <button
                                        type="button"
                                        className="v3-sadmin-page-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                                        disabled={currentPage === 0}
                                    >
                                        <ChevronLeft size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        className="v3-sadmin-page-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}
                                        disabled={currentPage >= pageCount - 1}
                                    >
                                        <ChevronRight size={13} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>
        </div>
    );
};

export default DataDeleteLog;
